import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { apiFetch } from './api';

const STORAGE_PREFIX = 'strip:lastSeen';
const CHECK_INTERVAL_MS = 60000;

export default function useStripBadge(userId, { enabled = true } = {}) {
  const [hasNew, setHasNew] = useState(false);
  const latestRef = useRef(0);
  const lastSeenRef = useRef(0);
  const storageKey = useMemo(() => (
    userId && enabled ? `${STORAGE_PREFIX}:${userId}` : null
  ), [userId, enabled]);

  useEffect(() => {
    if (!storageKey) {
      setHasNew(false);
      latestRef.current = 0;
      lastSeenRef.current = 0;
      return;
    }
    let stored = 0;
    try {
      stored = Number(localStorage.getItem(storageKey) || 0);
    } catch {
      stored = 0;
    }
    lastSeenRef.current = stored;
  }, [storageKey]);

  useEffect(() => {
    if (!storageKey) return;
    let cancelled = false;

    const check = async () => {
      try {
        const res = await apiFetch('/api/strip/feed?limit=1&offset=0');
        if (!res.ok) return;
        const data = await res.json();
        const posts = Array.isArray(data?.posts)
          ? data.posts
          : Array.isArray(data)
            ? data
            : [];
        const createdAt = posts[0]?.created_at ? new Date(posts[0].created_at).getTime() : 0;
        latestRef.current = createdAt;
        if (cancelled) return;
        const lastSeen = lastSeenRef.current || 0;
        setHasNew(Boolean(createdAt && createdAt > lastSeen));
      } catch {
        // ignore polling errors
      }
    };

    check();
    const timer = setInterval(check, CHECK_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [storageKey]);

  const markSeen = useCallback((timestamp) => {
    if (!storageKey) return;
    const value = Number(timestamp) || latestRef.current || Date.now();
    const next = Math.max(value, lastSeenRef.current || 0);
    lastSeenRef.current = next;
    setHasNew(false);
    try {
      localStorage.setItem(storageKey, String(next));
    } catch {
      // ignore storage errors
    }
  }, [storageKey]);

  return { hasNew, markSeen };
}
